/*
 * TEMPORARY STOP-GAP — delete this file, and its one call in GoogleAnalyticsEventService, once every
 * quiz has been rebuilt on MixedOnlineTests 6.0.0 or later, the release that sends GA4 event names
 * itself.
 *
 * Quiz clients older than 6.0.0 post events named like "MixedOnlineTest Completed", with
 * camelCase fields. Neither is valid in GA4. This renames them to the names and parameters newer
 * clients send, so a report sees one event whichever client a quiz was built with.
 *
 * Every translated event carries legacy_translated: true. When GA4 stops receiving events with it,
 * no quiz is still running an old client and this can go.
 */

interface LegacyEventTranslation {
  name: string;
  // Parameters the old client expressed through its event name, e.g. "Filtered Quiz Ended"
  params?: Record<string, unknown>;
}

const LEGACY_EVENTS: Record<string, LegacyEventTranslation> = {
  'MixedOnlineTest Started': {name: 'quiz_start'},
  'MixedOnlineTest Completed': {name: 'quiz_complete'},
  'MixedOnlineTest Cancelled': {name: 'quiz_cancel'},
  'Question Viewed': {name: 'question_view'},
  'RevisionSummaryTest Started': {name: 'summary_test_start'},
  'RevisionSummaryTest Question Answered': {name: 'summary_test_question_answer'},
  'RevisionSummaryTest Question Marked': {name: 'summary_test_question_mark'},
  'RevisionSummaryTest Question Review Mode Left': {name: 'summary_test_review_exit'},
  'RevisionSummaryTest Completed': {name: 'summary_test_complete'},
  'Quiz Started': {name: 'epwb_start'},
  'Quiz Exited': {name: 'epwb_exit'},
  'Unfiltered Quiz Ended': {name: 'epwb_complete', params: {filtered: false}},
  'Filtered Quiz Ended': {name: 'epwb_complete', params: {filtered: true}},
  'Question Marked': {name: 'epwb_question_mark', params: {free_sample: false}},
  'Free Sample Question Marked': {name: 'epwb_question_mark', params: {free_sample: true}},
  'Reset Question': {name: 'epwb_question_reset'},
  'Flex Menu Layer Click': {name: 'flex_menu_select'},
  'Reset All Questions': {name: 'questions_reset_all'},
};

// Old field names whose new name is not just the snake_case of the old one
const RENAMED_FIELDS: Record<string, string> = {
  questionSmileyMark: 'smiley_mark',
};

// Fields old clients sent that new clients don't: covered by others (time_taken_seconds, the
// event's own timestamp) or not used in reports. Dropped to stay within GA4's 25 parameters.
const DROPPED_FIELDS = new Set([
  'elapsedSeconds', 'elapsedMinutes', 'attemptDate', 'timeZoneOffset', 'localVersion', 'tagOptions', 'testTitle',
]);

export function isLegacyQuizEvent(analyticsEvent: Record<string, unknown>): boolean {
  return typeof analyticsEvent['event'] === 'string' && analyticsEvent['event'] in LEGACY_EVENTS;
}

export function translateLegacyQuizEvent(analyticsEvent: Record<string, unknown>): Record<string, unknown> {
  const {event, ...fields} = analyticsEvent;
  const translation = LEGACY_EVENTS[event as string];

  const translated: Record<string, unknown> = {
    event: translation.name,
    ...translation.params,
    legacy_translated: true,
  };

  for (const [key, value] of Object.entries(fields)) {
    if (DROPPED_FIELDS.has(key)) continue;
    translated[RENAMED_FIELDS[key] ?? toSnakeCase(key)] = value;
  }

  return translated;
}

function toSnakeCase(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}
